import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import error404 from 'assets/images/errors/error-404.webp';
import errorConnection from 'assets/images/errors/error-connection.webp';
import errorTechnical from 'assets/images/errors/error-technical.webp';
import { HelmetMetaTags } from 'shared/components/HelmetMetaTags';
import { APP_URL, ERROR_TYPES } from '../../constants/common';
import Button from '../form/button';

interface ErrorPageProps {
	errorType?: string | undefined;
}

const ErrorPage = (props: ErrorPageProps) => {
	const { errorType } = props;
	const [counter, setCounter] = useState(5);
	const history = useHistory();
	const { t } = useTranslation();

	const { err404, connectionLost, technicalBreak } = ERROR_TYPES;

	const getErrorData = (errorType: string) => {
		switch (errorType) {
			case err404:
				return {
					label: '404',
					description: t('warning404Title'),
					image: error404,
				};
			case connectionLost:
				return {
					label: 'Ups...',
					description: `${t('checkConnection')}`,
					image: errorConnection,
				};
			case technicalBreak:
				return {
					label: `${t('break')}`,
					description: `${t('technicalDifficulties')}`,
					image: errorTechnical,
				};
			default:
				return {
					label: `${t('break')}`,
					description: `${t('technicalDifficulties')}`,
					image: errorTechnical,
				};
		}
	};

	const errorData = getErrorData(errorType ? errorType : '');

	const { label, description, image } = errorData;

	const is404 = errorType === '404';

	const handleButtonClick = () => {
		window.location.assign('/');
	};

	useEffect(() => {
		const interval = setInterval(() => {
			setCounter(counter - 1);
		}, 1000);

		if (is404) counter === 0 && history.push(APP_URL.home);

		return () => {
			clearInterval(interval);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [counter]);

	const refreshButton = (
		<Button btnType="transparent" cyTestRef="404-refresh" className="button btn--transparent" onClick={handleButtonClick}>
			{t('refreshPage')}
		</Button>
	);

	return (
		<>
			{is404 && <HelmetMetaTags title="Error404" is404={true} />}
			<div className={`error-page ${is404 && 'error-page--404'}`}>
				<div className={`error-page__img ${is404 && 'error-page__img--404 '}`}>
					<img src={image} alt="error" className="width--full" />
				</div>
				<div className="error-page__text">
					<div className="error-page__label">{label}</div>
					<p className="error-page__description">{description}</p>
					{is404 && (
						<p
							className="text--midnight-green mt--0 mb--10"
							dangerouslySetInnerHTML={{
								__html: t('waring404', { count: counter }),
							}}
						/>
					)}
					{!is404 && refreshButton}
				</div>
			</div>
		</>
	);
};

export default ErrorPage;
